import { atom, selector } from 'recoil';
import {
  Disbursement,
  Donation,
  Invoice,
  Order,
  OrganizationReport,
  ReportOverview,
} from '@givz/core-client';
import {
  FormattedDonation,
  FormattedOrganizationReport,
  FormattedQualifiedOrdersReport,
  FormattedReportOverview,
  PerformanceQueryParams,
} from '../../types';

export const overviewReportState = atom<ReportOverview | undefined>({
  default: undefined,
  key: 'overviewReportState',
});

export const performanceSearchState = atom<PerformanceQueryParams | undefined>({
  default: undefined,
  key: 'performanceSearchState',
});

export const formattedOverviewReportSelector = selector<
  FormattedReportOverview | undefined
>({
  get: ({ get }) => {
    const overviewReport = get(overviewReportState);
    let formattedOverviewReport: FormattedReportOverview | undefined;

    if (overviewReport) {
      const currencyFormatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      });
      const amountDonated = currencyFormatter.format(
        overviewReport.amountDonated,
      );
      const salesGenerated = currencyFormatter.format(
        overviewReport.salesGenerated,
      );

      let redemptionRate = 'N/A';

      if (overviewReport.redemptionRate !== null) {
        let redemptionRatePercentage: string | number =
          overviewReport.redemptionRate * 100;

        if (redemptionRatePercentage.toString().length > 3) {
          redemptionRatePercentage = redemptionRatePercentage.toFixed(1);
        }

        redemptionRate = `${redemptionRatePercentage}%`;
      }

      formattedOverviewReport = {
        ...overviewReport,
        amountDonated,
        redemptionRate,
        salesGenerated,
      };
    }

    return formattedOverviewReport;
  },
  key: 'formattedOverviewReportSelector',
});

export const donationsReportState = atom<Donation[]>({
  default: [],
  key: 'donationsReportState',
});

export const formattedDonationsReportSelector = selector<FormattedDonation[]>({
  get: ({ get }) => {
    const donationsReport = get(donationsReportState);
    let formattedDonationsReport: FormattedDonation[] = [];

    if (donationsReport) {
      formattedDonationsReport = donationsReport.map((donation) => {
        return formatDonation(donation);
      });
    }

    return formattedDonationsReport;
  },
  key: 'formattedDonationsReportSelector',
});

export const OrganizationReportState = atom<OrganizationReport[]>({
  default: [],
  key: 'OrganizationReportState',
});

export const formattedOrganizationReportSelector = selector<
  FormattedOrganizationReport[]
>({
  get: ({ get }) => {
    const OrganizationReport = get(OrganizationReportState);
    let formattedOrganizationReport: FormattedOrganizationReport[] = [];

    if (OrganizationReport) {
      const currencyFormatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      });

      formattedOrganizationReport = OrganizationReport.map((organization) => {
        const amountDonated = currencyFormatter.format(
          organization.amountDonated,
        );

        return {
          ...organization,
          amountDonated,
        };
      });
    }

    return formattedOrganizationReport;
  },
  key: 'formattedOrganizationReportSelector',
});

export const qualifiedOrdersReportState = atom<Order[]>({
  default: [],
  key: 'qualifiedOrdersReportState',
});

export const formattedQualifiedOrdersReportSelector = selector<
  FormattedQualifiedOrdersReport[]
>({
  get: ({ get }) => {
    const qualifiedOrdersReport = get(qualifiedOrdersReportState);
    let formattedQualifiedOrdersReport: FormattedQualifiedOrdersReport[] = [];

    if (qualifiedOrdersReport) {
      const currencyFormatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
      });

      formattedQualifiedOrdersReport = qualifiedOrdersReport.map((order) => {
        const total = currencyFormatter.format(order.total);
        const purchasedAt = new Date(order.purchasedAt).toLocaleDateString();
        let donation: FormattedDonation | undefined;

        if (order.donation) {
          donation = formatDonation(order.donation);
        }

        return {
          ...order,
          donation,
          purchasedAt,
          total,
        };
      });
    }

    return formattedQualifiedOrdersReport;
  },
  key: 'formattedQualifiedOrdersReportSelector',
});

const formatDonation = (donation: Donation) => {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style: 'currency',
  });

  const donationAmount = currencyFormatter.format(donation.amountDonated);
  const pledgedAmount = currencyFormatter.format(donation.amountPledged);
  const createdAt = new Date(donation.createdAt).toLocaleDateString();
  const status = donation.status[0] + donation.status.slice(1).toLowerCase();
  const fee = currencyFormatter.format(donation.amountRaked);

  let invoice: Invoice | undefined;

  if (donation.invoice) {
    invoice = {
      ...donation.invoice,
      issuedAt: donation.invoice.issuedAt
        ? new Date(donation.invoice.issuedAt).toLocaleDateString()
        : undefined,
      paidAt: donation.invoice.paidAt
        ? new Date(donation.invoice.paidAt).toLocaleDateString()
        : undefined,
    };
  }

  let disbursement: Disbursement | undefined;

  if (donation.disbursement) {
    disbursement = {
      ...donation.disbursement,
      disbursedAt: donation.disbursement.disbursedAt
        ? new Date(donation.disbursement.disbursedAt).toLocaleDateString()
        : undefined,
    };
  }

  return {
    ...donation,
    createdAt,
    disbursement,
    donationAmount,
    fee,
    gid: donation.gid,
    invoice,
    pledgedAmount,
    status,
  };
};
